import React, { useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Box, Button, ButtonGroup, DropdownMenu, Flex, Menu, MenuItem, Popover, Text, Tooltip } from 'core/components';
import { TbChevronLeft, TbChevronRight, TbClockHour8, TbRefresh, TbRotateClockwise } from 'react-icons/tb';
import { InputGroup, Position } from '@blueprintjs/core';
import { DateRange, DayPicker, TZDate } from '@daypicker/react';
import { TimeRange } from '@kentik/ui-shared/query/query.types';
import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import TimezoneSelect from 'core/components/TimezoneSelect';
import { formatDateTime, getTimezone, getTimezoneShortName } from 'core/util/timezone';

/**
 * Time range options for the date picker
 * value is in seconds
 */
const TIME_RANGE_OPTIONS = [
  { label: 'Last 5 minutes', value: 5 * 60 },
  { label: 'Last 15 minutes', value: 15 * 60 },
  { label: 'Last 1 hour', value: 60 * 60 },
  { label: 'Last 3 hours', value: 3 * 60 * 60 },
  { label: 'Last 6 hours', value: 6 * 60 * 60 },
  { label: 'Last 1 day', value: 24 * 60 * 60 },
  { label: 'Last 2 days', value: 2 * 24 * 60 * 60 },
  { label: 'Last 3 days', value: 3 * 24 * 60 * 60 },
  { label: 'Last 1 week', value: 7 * 24 * 60 * 60 }
] as const;

/**
 * Auto refresh interval options
 * value is in seconds
 */
const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '5s', value: 5 },
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '5m', value: 5 * 60 },
  { label: '30m', value: 30 * 60 }
] as const;

/**
 * Format a lookback value in seconds to a human-readable string
 */
function formatLookback(seconds: number): string {
  const humanized = moment.duration(seconds, 'seconds').humanize();
  const normalized = humanized.replace(/^an? /, '1 ');
  return `Last ${normalized}`;
}

function parseDateTimeInput(value: string, timezone: string): TZDate | null {
  const parsed = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value);
  if (parsed) {
    const [_, year, month, day, hour, minute] = parsed;
    return new TZDate(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      timezone
    );
  }
  return null;
}

interface TimeNavigationControlProps {
  timeRange: TimeRange;
  timezone?: string;
  autoRefresh: number;
  hasRunQuery?: boolean;
  isLoading?: boolean;
  onTimeChange: (time: TimeRange) => void;
  onTimezoneChange?: (timezone: string) => void;
  onLiveUpdateChange: (updateFrequency: number) => void;
  onRefresh: () => void;
  extraMenuContent?: React.ReactNode;
}

const TimeNavigationControl = observer(
  ({
    timeRange = { lookback: 3 * 60 * 60, start: 0, end: 0 },
    timezone: timezoneValue = 'UTC',
    autoRefresh = 0,
    hasRunQuery = true,
    isLoading = false,
    onTimeChange,
    onTimezoneChange,
    onLiveUpdateChange,
    onRefresh,
    extraMenuContent
  }: TimeNavigationControlProps) => {
    const [isTimeMenuOpen, setIsTimeMenuOpen] = useState(false);
    const [dayPickerRange, setDayPickerRange] = useState<DateRange | undefined>(undefined);
    const [pendingStart, setPendingStart] = useState<TZDate | undefined>(undefined);
    const [pendingEnd, setPendingEnd] = useState<TZDate | undefined>(undefined);
    const [startInputValue, setStartInputValue] = useState('');
    const [endInputValue, setEndInputValue] = useState('');

    const timezone = getTimezone(timezoneValue);
    const timezoneDisplay = getTimezoneShortName(timezone, timeRange.end ? timeRange.end * 1000 : Date.now());
    const today = TZDate.tz(timezone);
    const duration = timeRange.lookback || timeRange.end - timeRange.start;
    const isSliding = !!timeRange.start && !!timeRange.end;

    const handleTimeRangeChange = (lookback: number) => {
      onTimeChange({ lookback, start: 0, end: 0 });
      setIsTimeMenuOpen(false);
    };

    const handleResetToNow = () => {
      onTimeChange({ lookback: duration, start: 0, end: 0 });
    };

    const handleSlideBack = () => {
      const now = Math.floor(Date.now() / 1000);

      let newEnd: number;
      let newStart: number;

      if (isSliding && timeRange.start !== null) {
        newEnd = timeRange.start;
        newStart = newEnd - duration;
      } else {
        newEnd = now - duration;
        newStart = newEnd - duration;
      }

      onTimeChange({ lookback: 0, start: newStart, end: newEnd });
    };

    const handleSlideForward = () => {
      const now = Math.floor(Date.now() / 1000);

      if (!isSliding || timeRange.end === null) {
        return;
      }

      const newStart = timeRange.end;
      const newEnd = newStart + duration;

      if (newEnd >= now) {
        handleResetToNow();
        return;
      }

      onTimeChange({ lookback: 0, start: newStart, end: newEnd });
    };

    const handleLiveUpdateChange = (seconds: number) => {
      onLiveUpdateChange(seconds);
    };

    const handleTimeMenuInteraction = (nextOpenState: boolean) => {
      setIsTimeMenuOpen(nextOpenState);

      if (nextOpenState) {
        const start = timeRange.start ? timeRange.start * 1000 : Date.now() - duration * 1000;
        const end = timeRange.end ? timeRange.end * 1000 : Date.now();
        const startDate = new TZDate(start, timezone);
        const endDate = new TZDate(end, timezone);
        setDayPickerRange({ from: startDate, to: endDate });
        setPendingStart(startDate);
        setPendingEnd(endDate);
        setStartInputValue(formatDateTime(startDate, timezone));
        setEndInputValue(formatDateTime(endDate, timezone));
      }
    };

    const handleApplyCustomRange = () => {
      if (pendingStart && pendingEnd) {
        onTimeChange({
          lookback: 0,
          start: Math.floor(pendingStart.getTime() / 1000),
          end: Math.floor(pendingEnd.getTime() / 1000)
        });
      }
      setIsTimeMenuOpen(false);
    };

    const handleCancelCustomRange = () => {
      setIsTimeMenuOpen(false);
    };

    const handleDayPickerSelect = (range?: DateRange) => {
      const start = range?.from;
      const end = range?.to;

      setDayPickerRange(range);

      if (start) {
        const startDate = new TZDate(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
          pendingStart?.getHours() || 0,
          pendingStart?.getMinutes() || 0,
          timezone
        );
        setPendingStart(startDate);
        setStartInputValue(formatDateTime(startDate, timezone));
      }

      if (end) {
        const endDate = new TZDate(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          pendingEnd?.getHours() || 0,
          pendingEnd?.getMinutes() || 0,
          timezone
        );
        setPendingEnd(endDate);
        setEndInputValue(formatDateTime(endDate, timezone));
      }
    };

    const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setStartInputValue(value);
      const parsed = parseDateTimeInput(value, timezone);
      if (parsed) {
        setPendingStart(parsed);
        setDayPickerRange({ from: parsed, to: pendingEnd });
      }
    };

    const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setEndInputValue(value);
      const parsed = parseDateTimeInput(value, timezone);
      if (parsed) {
        setPendingEnd(parsed);
        setDayPickerRange({ from: pendingStart, to: parsed });
      }
    };

    const handleTimezoneChange = (newTimezoneValue: string) => {
      const newTimezone = getTimezone(newTimezoneValue);
      const start = timeRange.start ? timeRange.start * 1000 : Date.now() - duration * 1000;
      const end = timeRange.end ? timeRange.end * 1000 : Date.now();
      const startDate = new TZDate(start, newTimezone);
      const endDate = new TZDate(end, newTimezone);

      setDayPickerRange({ from: startDate, to: endDate });
      setPendingStart(startDate);
      setPendingEnd(endDate);
      setStartInputValue(formatDateTime(startDate, newTimezone));
      setEndInputValue(formatDateTime(endDate, newTimezone));

      if (onTimezoneChange) {
        onTimezoneChange(newTimezoneValue);
      }
    };

    /**
     * Get the display label for the current auto-refresh interval
     */
    const getAutoRefreshDisplay = () => {
      if (autoRefresh === 0) {
        return 'Off';
      }
      if (autoRefresh < 60) {
        return `${autoRefresh}s`;
      }
      if (autoRefresh < 3600) {
        return `${autoRefresh / 60}m`;
      }
      return `${autoRefresh / 3600}h`;
    };

    const getTimeDisplay = () => {
      if (isSliding) {
        const start = new Date(timeRange.start * 1000);
        const end = new Date(timeRange.end * 1000);
        const range = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour12: false,
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).formatRange(start, end);
        return `${range} ${timezoneDisplay}`;
      }
      return formatLookback(timeRange.lookback);
    };

    const canApply = !!pendingStart && !!pendingEnd && pendingStart < pendingEnd;

    return (
      <>
        <ButtonGroup>
          <Tooltip content="Slide window back" position={Position.BOTTOM}>
            <Button icon={TbChevronLeft} onClick={handleSlideBack} disabled={isLoading} />
          </Tooltip>

          <Popover position={Position.BOTTOM_LEFT} isOpen={isTimeMenuOpen} onInteraction={handleTimeMenuInteraction}>
            <Button
              icon={TbClockHour8}
              rightIcon="caret-down"
              text={<Text fontWeight="medium">{getTimeDisplay()}</Text>}
            />
            <Box>
              <Flex>
                <Box minWidth={200}>
                  <Menu>
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        text={option.label}
                        active={!isSliding && timeRange.lookback === option.value}
                        onClick={() => handleTimeRangeChange(option.value)}
                      />
                    ))}
                  </Menu>
                  {extraMenuContent}
                </Box>

                <Box borderLeft="thin" p={1} width={473}>
                  <Flex gap="4px" justifyContent="flex-end" mb={1}>
                    <Button text="Cancel" minWidth={65} onClick={handleCancelCustomRange} />
                    <Button
                      text="Apply"
                      intent="primary"
                      minWidth={65}
                      disabled={!canApply}
                      onClick={handleApplyCustomRange}
                    />
                  </Flex>
                  <Flex gap={1} mb={1}>
                    <Box flex="1 1 50%">
                      <Text small muted mb="4px">
                        From ({timezoneDisplay})
                      </Text>
                      <InputGroup
                        value={startInputValue}
                        onChange={handleStartInputChange}
                        placeholder={DEFAULT_DATETIME_FORMAT}
                      />
                    </Box>
                    <Box flex="1 1 50%">
                      <Text small muted mb="4px">
                        To ({timezoneDisplay})
                      </Text>
                      <InputGroup
                        value={endInputValue}
                        onChange={handleEndInputChange}
                        placeholder={DEFAULT_DATETIME_FORMAT}
                      />
                    </Box>
                  </Flex>
                  <DayPicker
                    mode="range"
                    captionLayout="dropdown"
                    navLayout="around"
                    numberOfMonths={2}
                    required={false}
                    resetOnSelect
                    selected={dayPickerRange}
                    timeZone={timezone}
                    onSelect={handleDayPickerSelect}
                    endMonth={today}
                    disabled={{ after: today }}
                  />
                </Box>
              </Flex>
              {onTimezoneChange && (
                <Flex gap={1} p={1} alignItems="center" borderTop="thin">
                  <Text id="board-tz-label">Timezone</Text>
                  <Box flex={1}>
                    <TimezoneSelect value={timezoneValue} onChange={handleTimezoneChange} labelId="board-tz-label" />
                  </Box>
                </Flex>
              )}
            </Box>
          </Popover>

          <Tooltip content="Slide window forward" position={Position.BOTTOM}>
            <Button icon={TbChevronRight} onClick={handleSlideForward} disabled={isLoading || !isSliding} />
          </Tooltip>

          {isSliding && (
            <Tooltip content="Jump to now" position={Position.BOTTOM}>
              <Button icon={TbRotateClockwise} onClick={handleResetToNow} disabled={isLoading} />
            </Tooltip>
          )}
        </ButtonGroup>

        {hasRunQuery && (
          <ButtonGroup>
            <Tooltip content="Refresh" position={Position.BOTTOM}>
              <Button icon={TbRefresh} onClick={onRefresh} disabled={!hasRunQuery || isLoading} />
            </Tooltip>

            <DropdownMenu position={Position.BOTTOM_LEFT}>
              <Tooltip
                content={`Auto refresh ${autoRefresh ? 'enabled' : 'disabled'}`}
                position={Position.BOTTOM_RIGHT}
              >
                <Button rightIcon="caret-down" text={getAutoRefreshDisplay()} />
              </Tooltip>
              <Box minWidth={160}>
                <Menu>
                  {AUTO_REFRESH_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      text={option.label}
                      onClick={() => handleLiveUpdateChange(option.value)}
                    />
                  ))}
                </Menu>
              </Box>
            </DropdownMenu>
          </ButtonGroup>
        )}
      </>
    );
  }
);

export default TimeNavigationControl;
